import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { ServiceRequest } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import {
  Accordion,
  Anchor,
  Button,
  Flex,
  Group,
  Pagination,
  Paper,
  Stack,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconPlus, IconTestPipe } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { PatientContext } from "@/contexts/patient";
import { ProfileContext } from "@/contexts/profile";
import { AddLabOrderDrawer } from "@/health-gorilla";
import HealthGorillaRequiredFieldsAlert from "@/health-gorilla/components/HealthGorillaRequiredFieldsAlert";
import LabsOrderInactiveAlert from "@/health-gorilla/components/LabsOrderInactiveAlert";
import { useRefreshPatientForLabsBanner } from "@/health-gorilla/hooks/useRefreshPatientForLabsBanner";
import {
  fetchIsLabOrdersIntegrationActive,
  fetchLabOrderResultsForEncounter,
} from "@/health-gorilla/services/labs";
import { hasRequiredFieldsForHealthGorilla } from "@/health-gorilla/utils/labs";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { IntakeContentRemovedAlert, IntakeDependentFeature } from "@/settings";
import { RootState, useAppDispatch } from "@/store";
import { loadLabOrders, selectLabOrdersForEncounter } from "@/store/slices/lab-orders";
import usePagination from "@/utils/hooks/use-pagination";

import LabOrderItem from "./LabOrderItem";
import classes from "./LabOrdersList.module.css";

export const PAGE_SIZE = 10;

interface LabOrdersListProps {
  isEncounterActive: boolean;
  isAmendment?: boolean;
}

export default function LabOrdersList({ isEncounterActive, isAmendment }: LabOrdersListProps) {
  const medplum = useMedplum();
  const [isAddLabOrderDrawerOpen, { open: openAddLabOrderDrawer, close: closeAddLabOrderDrawer }] =
    useDisclosure(false);
  const [isLabOrdersIntegrationActive, setIsLabOrdersIntegrationActive] = useState(false);
  const { clientId, encounterId } = useParams<{ clientId: string; encounterId: string }>();
  const dispatch = useAppDispatch();
  const labOrders = useSelector<RootState, ServiceRequest[]>(
    selectLabOrdersForEncounter(encounterId),
  );
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(true);
  const { hasLabOrderPermission } = usePractitionerPermissions();
  const { patient } = useContext(PatientContext);
  const { isPatient } = useContext(ProfileContext);
  const handlePageChange = useCallback(() => {
    startLoading();
  }, [startLoading]);

  // TODO[LABS]: Ensure pagination is working correctly
  const { currentPage, changeCurrentPage, totalPages, changeTotalPages } = usePagination({
    pageSize: PAGE_SIZE,
    onChange: handlePageChange,
  });

  useEffect(() => {
    if (!encounterId || !clientId) return;

    startLoading();
    (async () => {
      try {
        const { results, total } = await fetchLabOrderResultsForEncounter(
          medplum,
          clientId,
          currentPage,
          PAGE_SIZE,
          encounterId,
        );
        setIsLabOrdersIntegrationActive(await fetchIsLabOrdersIntegrationActive(medplum));
        dispatch(loadLabOrders(results));
        changeTotalPages(total);
      } finally {
        stopLoading();
      }
    })();
  }, [
    medplum,
    startLoading,
    stopLoading,
    currentPage,
    changeTotalPages,
    dispatch,
    clientId,
    encounterId,
    patient,
  ]);

  const { refreshPatient, isRefreshing: isRefreshingPatient } = useRefreshPatientForLabsBanner({
    refreshOnMount: true,
    refreshOnTabFocus: true,
  });

  const [requiredHealthGorillaFields, canUseHealthGorilla] = useMemo(() => {
    const requiredFields = hasRequiredFieldsForHealthGorilla(patient);
    return [requiredFields, Object.values(requiredFields).every((value) => value)];
  }, [patient]);
  const hasHealthGorillaDataGap =
    !isLoading && !canUseHealthGorilla && !isPatient && isLabOrdersIntegrationActive;

  const showAddMoreButton = isEncounterActive && !isAmendment && hasLabOrderPermission;
  return (
    <PermissionGate requiredPermissions={PROVIDER_SCRIBE_PERMISSION_FLAGS}>
      {showAddMoreButton && canUseHealthGorilla && (
        <AddLabOrderDrawer
          encounterId={encounterId}
          opened={isAddLabOrderDrawerOpen}
          onClose={closeAddLabOrderDrawer}
        />
      )}
      <Stack pt="xl" gap="lg">
        <Paper p={24} radius="lg">
          <Group pb="md" align="center" justify="space-between">
            <Title order={3} c="burgundy.9">
              Lab Orders
            </Title>
            {showAddMoreButton && (
              <Button
                variant="outline"
                color="burgundy.9"
                onClick={openAddLabOrderDrawer}
                disabled={!canUseHealthGorilla}
                leftSection={<IconPlus size={14} />}
              >
                Add More
              </Button>
            )}
          </Group>

          {isLoading && (
            <Flex h="100%" align="center" justify="center">
              <Loading height={48} />
            </Flex>
          )}
          {!isLoading && !isLabOrdersIntegrationActive && !isPatient && (
            <LabsOrderInactiveAlert mb="md" />
          )}
          {hasHealthGorillaDataGap && (
            <HealthGorillaRequiredFieldsAlert
              mb="md"
              refreshPatient={refreshPatient}
              isRefreshing={isRefreshingPatient}
              requiredFields={requiredHealthGorillaFields}
            />
          )}
          {hasHealthGorillaDataGap && clientId && (
            <IntakeContentRemovedAlert
              mb="md"
              clientId={clientId}
              feature={IntakeDependentFeature.Labs}
            />
          )}
          {!isLoading && labOrders.length > 0 && (
            <>
              <Accordion multiple classNames={{ control: classes.control }}>
                <Stack gap="xs">
                  {labOrders.map((labOrder) => (
                    <LabOrderItem key={labOrder.id} labOrder={labOrder} />
                  ))}
                </Stack>
              </Accordion>
              <Flex justify="center" mt={totalPages > 1 ? "md" : 0}>
                <Pagination
                  hideWithOnePage
                  total={totalPages}
                  value={currentPage}
                  onChange={changeCurrentPage}
                  data-testid="labOrdersPagination"
                />
              </Flex>
            </>
          )}
          {!isLoading && labOrders.length === 0 && (
            <Flex
              h={40}
              gap="sm"
              w="100%"
              bg="gray.0"
              align="center"
              justify="center"
              style={{ borderRadius: "var(--mantine-radius-xs)" }}
            >
              <IconTestPipe size={18} color="var(--mantine-color-gray-6)" />
              <TextWrapper fz="md" c="gray.7" variant="default">
                No lab orders have been added.
                {showAddMoreButton && canUseHealthGorilla && (
                  <Anchor
                    ml={4}
                    fw="bold"
                    c="gray.7"
                    underline="always"
                    style={{ userSelect: "none" }}
                    onClick={openAddLabOrderDrawer}
                  >
                    Add Lab Order
                  </Anchor>
                )}
              </TextWrapper>
            </Flex>
          )}
        </Paper>
      </Stack>
    </PermissionGate>
  );
}
